export interface LegalDocVersion {
  versionNumber: number;
  content: string;
  status: 'active' | 'draft' | 'archived';
  createdAt: string;
  createdBy: string;
  publishedAt: string | null;
  notes: string;
}

export interface LegalDoc {
  id: number;
  slug: string;
  title: string;
  icon: string;
  description: string;
  color: 'primary' | 'accent' | 'secondary' | 'emerald' | 'amber' | 'sky' | 'rose';
  currentVersion: number;
  lastPublishedAt: string;
  updatedAt: string;
  updatedBy: string;
  versions: LegalDocVersion[];
}

const COLOR_MAP: Record<string, { dot: string; bg: string; border: string; text: string; iconBg: string; iconText: string }> = {
  primary: { dot: 'bg-primary-500', bg: 'bg-primary-50', border: 'border-primary-200', text: 'text-primary-700', iconBg: 'bg-primary-100', iconText: 'text-primary-600' },
  accent: { dot: 'bg-accent-500', bg: 'bg-accent-50', border: 'border-accent-200', text: 'text-accent-700', iconBg: 'bg-accent-100', iconText: 'text-accent-600' },
  secondary: { dot: 'bg-secondary-500', bg: 'bg-secondary-50', border: 'border-secondary-200', text: 'text-secondary-700', iconBg: 'bg-secondary-100', iconText: 'text-secondary-600' },
  emerald: { dot: 'bg-emerald-500', bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', iconBg: 'bg-emerald-100', iconText: 'text-emerald-600' },
  amber: { dot: 'bg-amber-500', bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', iconBg: 'bg-amber-100', iconText: 'text-amber-600' },
  sky: { dot: 'bg-sky-500', bg: 'bg-sky-50', border: 'border-sky-200', text: 'text-sky-700', iconBg: 'bg-sky-100', iconText: 'text-sky-600' },
  rose: { dot: 'bg-red-500', bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', iconBg: 'bg-red-100', iconText: 'text-red-500' },
};

export const LEGAL_COLORS = COLOR_MAP;

function contentTerms(): string {
  return `<h2>Términos y Condiciones de Uso</h2>
<p><strong>Última actualización:</strong> 12 de junio de 2026.</p>
<h3>1. Aceptación de los Términos</h3>
<p>Al acceder o utilizar RadioFrecuenciaWeb (en adelante, "la Plataforma"), usted acepta de manera expresa e irrevocable los presentes Términos y Condiciones. Si no está de acuerdo con alguno de los términos aquí establecidos, debe abstenerse de utilizar la Plataforma.</p>
<h3>2. Alcance de los Servicios</h3>
<p>RadioFrecuenciaWeb es una plataforma de administración, gestión, promoción e interacción para emisoras de radio por internet. La Plataforma actúa como un centro de control integral que permite a las emisoras gestionar su presencia digital, interactuar con su audiencia, administrar su programación, y acceder a herramientas de análisis y moderación.</p>
<h3>3. Registro y Cuenta</h3>
<p>Para registrar una Radio en la Plataforma, es necesario completar el formulario de registro con información veraz y actualizada. Usted es responsable de mantener la confidencialidad de sus credenciales de acceso.</p>
<h3>4. Contenido Prohibido</h3>
<p>Queda estrictamente prohibido: contenido sexual explícito, discurso de odio, acoso, material violento, spam, violación de derechos de autor, suplantación de identidad, y cualquier actividad ilegal.</p>
<h3>5. Limitación de Responsabilidad</h3>
<p>RadioFrecuenciaWeb proporciona la Plataforma "tal cual" y "según disponibilidad". No garantizamos que el servicio sea ininterrumpido, libre de errores o completamente seguro.</p>`;
}

function contentPrivacy(): string {
  return `<h2>Política de Privacidad</h2>
<p><strong>Última actualización:</strong> 12 de junio de 2026.</p>
<h3>1. Introducción</h3>
<p>En RadioFrecuenciaWeb, la privacidad de nuestros usuarios es una prioridad. Esta Política describe cómo recopilamos, utilizamos, almacenamos y protegemos la información personal.</p>
<h3>2. Datos que Recopilamos</h3>
<p>Recopilamos: datos de registro (nombre, correo, frecuencia), datos de oyente (usuario, preferencias), datos técnicos (IP, navegador), y contenido generado por el usuario.</p>
<h3>3. Uso de la Información</h3>
<p>Utilizamos la información para proveer y mejorar la Plataforma, personalizar la experiencia, procesar registros, enviar notificaciones, y cumplir con obligaciones legales.</p>
<h3>4. Derechos del Usuario</h3>
<p>Tienes derecho a acceder, rectificar, suprimir, y portar tus datos. Para ejercer estos derechos, contacta a privacy@radiofrecuenciaweb.com.</p>`;
}

function contentAdvertising(): string {
  return `<h2>Política de Publicidad</h2>
<p><strong>Última actualización:</strong> 12 de junio de 2026.</p>
<h3>1. Principios Generales</h3>
<p>RadioFrecuenciaWeb ofrece espacios publicitarios dentro de la Plataforma para radios y anunciantes. Toda publicidad debe cumplir con estándares de transparencia, veracidad y respeto al usuario.</p>
<h3>2. Formatos Publicitarios</h3>
<p>Los formatos disponibles incluyen: banners laterales en página de radio, banners inline en home, anuncios en cabina de chat, patrocinios de eventos, y espacios destacados en el directorio.</p>
<h3>3. Contenido Publicitario Prohibido</h3>
<p>No se permite publicidad de: productos de tabaco, armas, contenido adulto, esquemas piramidales, criptomonedas no reguladas, medicamentos sin receta, ni servicios que violen los Términos de la Plataforma.</p>
<h3>4. Revenue Share</h3>
<p>Los ingresos por publicidad se distribuyen según el acuerdo de Revenue Share vigente. Consultá la sección de Revenue Share para conocer los porcentajes aplicables.</p>`;
}

function contentRevenueShare(): string {
  return `<h2>Acuerdo de Revenue Share</h2>
<p><strong>Última actualización:</strong> 12 de junio de 2026.</p>
<h3>1. Definición</h3>
<p>El Revenue Share es el mecanismo mediante el cual RadioFrecuenciaWeb distribuye los ingresos generados por publicidad entre la Plataforma y las Radios participantes.</p>
<h3>2. Distribución</h3>
<p>La distribución actual es: 70% para la Radio, 30% para RadioFrecuenciaWeb. Este porcentaje aplica sobre ingresos netos de publicidad en la página de cada radio.</p>
<h3>3. Elegibilidad</h3>
<p>Para participar en Revenue Share, la radio debe tener un plan activo (Pro, Premium o Business), mantener transmisiones regulares, y cumplir con las políticas de contenido.</p>
<h3>4. Pagos</h3>
<p>Los pagos se procesan mensualmente cuando el saldo acumulado supera los $50 USD. Se realizan a través de los procesadores de pago configurados en la Plataforma.</p>`;
}

function contentMarketplace(): string {
  return `<h2>Términos del Marketplace</h2>
<p><strong>Última actualización:</strong> 12 de junio de 2026.</p>
<h3>1. Descripción</h3>
<p>El Marketplace de RadioFrecuenciaWeb es un espacio donde las Radios y Anunciantes pueden conectar para comprar y vender espacios publicitarios directamente, sin intermediación de la Plataforma más allá de la provisión del espacio de conexión.</p>
<h3>2. Funcionamiento</h3>
<p>Los Anunciantes pueden explorar el catálogo de Radios disponibles, filtrar por audiencia, género, ubicación y métricas. Las Radios publican sus espacios disponibles con precios y condiciones.</p>
<h3>3. Transacciones</h3>
<p>Las transacciones se realizan directamente entre la Radio y el Anunciante. RadioFrecuenciaWeb no es parte del contrato publicitario y no asume responsabilidad por el cumplimiento de las partes.</p>
<h3>4. Comisión de Plataforma</h3>
<p>RadioFrecuenciaWeb retiene una comisión del 8% sobre cada transacción completada a través del Marketplace, que se descuenta automáticamente del pago.</p>`;
}

function contentLicenses(): string {
  return `<h2>Licencias y Derechos</h2>
<p><strong>Última actualización:</strong> 20 de junio de 2026.</p>

<h3>1. Licencia de Plataforma</h3>
<p>RadioFrecuenciaWeb otorga a cada Radio una licencia limitada, no exclusiva, no transferible y revocable para utilizar la Plataforma de acuerdo con estos términos y el plan contratado.</p>

<h3>2. Licencia Anual RFW</h3>
<p>La Licencia Anual de Frecuencia RFW es un cargo anual obligatorio que garantiza la reserva exclusiva de la frecuencia asignada dentro del directorio de la Plataforma. El costo actual es de $49.95 USD por año.</p>

<h3>3. Responsabilidad de Licencias — Exclusiva de la Radio</h3>
<p>Cada radio es la <strong>única y exclusiva responsable</strong> de obtener, mantener y renovar todas las licencias necesarias para operar legalmente. Esto incluye de manera taxativa:</p>
<ul>
<li><strong>Licencias musicales:</strong> derechos de ejecución pública, derechos mecánicos, derechos de sincronización y cualquier otra licencia requerida por entidades de gestión colectiva (SADAIC, ASCAP, BMI, SESAC, AGADU, SAYCO, APDAYC, etc.).</li>
<li><strong>Derechos de autor:</strong> permisos de los titulares de derechos sobre obras musicales, fonogramas, podcasts, programas, eventos deportivos y cualquier contenido protegido transmitido.</li>
<li><strong>Licencias de transmisión:</strong> habilitaciones de radiodifusión, licencias de streaming y cualquier permiso exigido por la autoridad regulatoria de su jurisdicción.</li>
<li><strong>Permisos regulatorios:</strong> inscripciones, registros y habilitaciones ante organismos de radiodifusión, telecomunicaciones y cualquier ente regulador competente.</li>
</ul>

<h3>4. Lo que RFW NO Provee</h3>
<p>RadioFrecuenciaWeb <strong>NO</strong> provee, gestiona, tramita ni asume responsabilidad alguna sobre:</p>
<ul>
<li>Licencias musicales ni derechos de autor.</li>
<li>Licencias de transmisión ni habilitaciones de radiodifusión.</li>
<li>Permisos regulatorios ni inscripciones ante organismos de control.</li>
<li>Servidores de streaming, SHOUTcast, Icecast, AutoDJ ni ancho de banda.</li>
</ul>
<p>RFW es exclusivamente una plataforma de administración digital. Cualquier sanción, multa, reclamo o acción legal derivada de la falta de licencias o permisos recae exclusivamente sobre la radio infractora.</p>

<h3>5. Propiedad Intelectual de la Plataforma</h3>
<p>El nombre "RadioFrecuenciaWeb", el logo, la interfaz, el código fuente y todos los elementos de la Plataforma son propiedad exclusiva de RadioFrecuenciaWeb.</p>`;
}

function contentContracts(): string {
  return `<h2>Marco Contractual</h2>
<p><strong>Última actualización:</strong> 20 de junio de 2026.</p>

<h3>1. Naturaleza del Servicio</h3>
<p>RadioFrecuenciaWeb (<strong>RFW</strong>) es una plataforma de administración integral para radios online. RFW <strong>NO</strong> es un proveedor de streaming de audio, ni un servidor SHOUTcast/Icecast, ni un servicio de AutoDJ, ni un proveedor de ancho de banda para transmisión radial.</p>

<h3>2. Qué Administra RFW</h3>
<p>RFW administra y provee herramientas en cinco pilares fundamentales:</p>
<ul>
<li><strong>Radio:</strong> perfil público, frecuencia RFW exclusiva, panel de control, programación, estadísticas.</li>
<li><strong>Comunidad:</strong> seguidores, chat en vivo, cabina interactiva, mensajería, moderación.</li>
<li><strong>Contenido:</strong> galería de fotos, videos, publicaciones, eventos, lives dentro de la plataforma.</li>
<li><strong>Audiencia:</strong> directorio de radios, búsqueda, descubrimiento, notificaciones, interacción oyente-radio.</li>
<li><strong>Monetización:</strong> espacios publicitarios, marketplace de anuncios, revenue share, patrocinios.</li>
</ul>

<h3>3. Streaming — Responsabilidad Exclusiva de la Radio</h3>
<p>Cada radio es responsable de contratar, configurar y mantener su propio servicio de streaming de audio con un proveedor externo. La radio proporciona su <strong>Stream URL</strong> para que RFW pueda reproducir su señal dentro de la plataforma. Si la URL falla, se cae, no transmite o el proveedor externo tiene problemas, RadioFrecuenciaWeb no es responsable por esa interrupción. La disponibilidad, estabilidad, calidad de audio, continuidad de transmisión y consumo de ancho de banda son responsabilidad exclusiva del proveedor de transmisión contratado por la emisora.</p>

<h3>4. Licencias y Derechos — Responsabilidad Exclusiva de la Radio</h3>
<p>Cada radio es la única responsable de obtener, mantener y cumplir con todos los permisos, licencias, regalías y autorizaciones correspondientes al contenido que difunde. Esto incluye, sin limitación:</p>
<ul>
<li>Licencias musicales y derechos de autor (SADAIC, ASCAP, BMI, SESAC, AGADU, SAYCO, APDAYC y cualquier otra entidad aplicable).</li>
<li>Licencias de transmisión y radiodifusión.</li>
<li>Permisos regulatorios ante organismos de radiodifusión de su jurisdicción.</li>
<li>Derechos de transmisión de eventos deportivos, podcasts, programas y cualquier contenido protegido.</li>
</ul>
<p>RFW no gestiona, no provee y no asume responsabilidad sobre licencias musicales, derechos de autor, permisos de transmisión ni habilitaciones regulatorias. Cualquier infracción es responsabilidad exclusiva de la radio infractora.</p>

<h3>5. Naturaleza del Vínculo</h3>
<p>La relación entre RadioFrecuenciaWeb y cada Radio se rige por un contrato de servicios digitales. No existe relación laboral, de sociedad, de agencia o de franquicia entre las partes.</p>

<h3>6. Duración y Renovación</h3>
<p>Los contratos tienen una duración inicial según el plan contratado (mensual o anual). Se renuevan automáticamente al finalizar cada período, salvo cancelación expresa con al menos 15 días de anticipación.</p>

<h3>7. Rescisión</h3>
<p>Cualquiera de las partes puede rescindir el contrato con un preaviso de 30 días. RadioFrecuenciaWeb puede rescindir inmediatamente en caso de violación grave de los Términos.</p>

<h3>8. Jurisdicción</h3>
<p>Este contrato se rige por las leyes del Estado de California, Estados Unidos. Cualquier disputa será resuelta en los tribunales competentes del Condado de Sonoma, California.</p>`;
}

function buildVersions(baseContent: string): LegalDocVersion[] {
  return [
    {
      versionNumber: 4,
      content: baseContent,
      status: 'active',
      createdAt: '2026-06-12T10:30:00',
      createdBy: 'Admin Global',
      publishedAt: '2026-06-12T10:35:00',
      notes: 'Actualización integral Q2 2026. Mejoras en claridad y cumplimiento normativo.',
    },
    {
      versionNumber: 3,
      content: baseContent.replace('12 de junio de 2026', '15 de marzo de 2026'),
      status: 'archived',
      createdAt: '2026-03-15T09:00:00',
      createdBy: 'Admin Global',
      publishedAt: '2026-03-15T09:10:00',
      notes: 'Revisión trimestral Q1 2026. Ajustes menores de redacción.',
    },
    {
      versionNumber: 2,
      content: baseContent.replace('12 de junio de 2026', '20 de diciembre de 2025'),
      status: 'archived',
      createdAt: '2025-12-20T14:00:00',
      createdBy: 'Carlos López',
      publishedAt: '2025-12-20T14:30:00',
      notes: 'Actualización de fin de año. Incorporación de nuevas políticas.',
    },
    {
      versionNumber: 1,
      content: baseContent.replace('12 de junio de 2026', '1 de septiembre de 2025'),
      status: 'archived',
      createdAt: '2025-09-01T08:00:00',
      createdBy: 'Carlos López',
      publishedAt: '2025-09-01T08:00:00',
      notes: 'Versión inicial. Lanzamiento de la Plataforma.',
    },
  ];
}

export const legalCenterDocs: LegalDoc[] = [
  {
    id: 1,
    slug: 'terminos',
    title: 'Términos y Condiciones',
    icon: 'ri-file-text-line',
    description: 'Condiciones generales de uso de la Plataforma. Aceptación, alcance de servicios, obligaciones y limitaciones.',
    color: 'primary',
    currentVersion: 4,
    lastPublishedAt: '2026-06-12T10:35:00',
    updatedAt: '2026-06-12T10:30:00',
    updatedBy: 'Admin Global',
    versions: buildVersions(contentTerms()),
  },
  {
    id: 2,
    slug: 'privacidad',
    title: 'Política de Privacidad',
    icon: 'ri-shield-check-line',
    description: 'Cómo recopilamos, usamos y protegemos los datos personales de usuarios y radios.',
    color: 'accent',
    currentVersion: 4,
    lastPublishedAt: '2026-06-12T10:35:00',
    updatedAt: '2026-06-12T10:30:00',
    updatedBy: 'Admin Global',
    versions: buildVersions(contentPrivacy()),
  },
  {
    id: 3,
    slug: 'publicidad',
    title: 'Política de Publicidad',
    icon: 'ri-advertisement-line',
    description: 'Lineamientos para anunciantes: formatos disponibles, contenido prohibido y estándares de transparencia.',
    color: 'secondary',
    currentVersion: 4,
    lastPublishedAt: '2026-06-12T10:35:00',
    updatedAt: '2026-06-12T10:30:00',
    updatedBy: 'Admin Global',
    versions: buildVersions(contentAdvertising()),
  },
  {
    id: 4,
    slug: 'revenue-share',
    title: 'Revenue Share',
    icon: 'ri-percent-line',
    description: 'Acuerdo de distribución de ingresos publicitarios entre la Plataforma y las Radios participantes.',
    color: 'emerald',
    currentVersion: 4,
    lastPublishedAt: '2026-06-12T10:35:00',
    updatedAt: '2026-06-12T10:30:00',
    updatedBy: 'Admin Global',
    versions: buildVersions(contentRevenueShare()),
  },
  {
    id: 5,
    slug: 'marketplace',
    title: 'Términos del Marketplace',
    icon: 'ri-store-2-line',
    description: 'Condiciones para la compra y venta de espacios publicitarios entre Radios y Anunciantes.',
    color: 'amber',
    currentVersion: 4,
    lastPublishedAt: '2026-06-12T10:35:00',
    updatedAt: '2026-06-12T10:30:00',
    updatedBy: 'Admin Global',
    versions: buildVersions(contentMarketplace()),
  },
  {
    id: 6,
    slug: 'licencias',
    title: 'Licencias y Derechos',
    icon: 'ri-key-2-line',
    description: 'Licencia de Plataforma, Licencia Anual RFW, derechos de autor y propiedad intelectual.',
    color: 'sky',
    currentVersion: 4,
    lastPublishedAt: '2026-06-12T10:35:00',
    updatedAt: '2026-06-12T10:30:00',
    updatedBy: 'Admin Global',
    versions: buildVersions(contentLicenses()),
  },
  {
    id: 7,
    slug: 'contratos',
    title: 'Marco Contractual',
    icon: 'ri-file-text-line',
    description: 'Naturaleza del vínculo, duración, renovación, rescisión y jurisdicción aplicable.',
    color: 'rose',
    currentVersion: 4,
    lastPublishedAt: '2026-06-12T10:35:00',
    updatedAt: '2026-06-12T10:30:00',
    updatedBy: 'Admin Global',
    versions: buildVersions(contentContracts()),
  },
];

export function getLegalDocBySlug(slug: string): LegalDoc | undefined {
  return legalCenterDocs.find((d) => d.slug === slug);
}

export function getActiveVersion(doc: LegalDoc): LegalDocVersion {
  return doc.versions.find((v) => v.status === 'active') || doc.versions[0];
}

export function getAllVersions(doc: LegalDoc): LegalDocVersion[] {
  return [...doc.versions].sort((a, b) => b.versionNumber - a.versionNumber);
}